import { useEffect, useMemo } from "react";

import { clearComposerDraft } from "~/ai-chat/data-source/composer-draft-store";
import { markConversationRead, useConversationReadMarks } from "~/ai-chat/data-source/conversation-read-store";
import { flattenConversations, useConversations } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useRenameChat } from "~/ai-chat/feature/use-rename-chat";
import type { Conversation } from "~/ai-chat/model/conversation";
import { hasUnseenUpdate } from "~/ai-chat/model/conversation-read-marks";
import type { ChatTab } from "~/ai-chat/ui/chat-tabs";

export interface ChatTabsState {
  tabs: ChatTab[];
  // Stable key of the on-screen tab (empty tab's client key, else the conversation id).
  // Changes on every real switch but not on an empty-tab keystroke — key the composer by it so its
  // per-tab UI state resets, and hand it over as `draftKey` so the tab's unsent text is restored.
  activeKey: string;
  switchTo: (key: string) => void;
  closeTab: (key: string) => void;
  startNew: () => void;
  renameTab: (key: string, title: string | null) => void;
}

// Open-chats state for the tab strip. Saved conversations opened this session are
// keyed by their id; unsaved new chats get a client-side key so several empty tabs
// coexist and survive a switch. Switching never stops a running chat (they stream in
// parallel — see useChatSend), and the status dot reads the live `runningConversationIds`
// so it flips to amber while a chat is processing. Pair with `useHydrateActiveConversation`.
export interface ChatTabsOptions {
  // False while the host shows something other than the conversation (the panel's
  // history list), so a turn that finishes meanwhile is not marked read unseen.
  conversationVisible?: boolean;
}

export function useChatTabs({ conversationVisible = true }: ChatTabsOptions = {}): ChatTabsState {
  const {
    activeConversationId,
    setActiveConversationId,
    chatTitle,
    setChatTitle,
    startNewConversation,
    clearMessages,
    markConversationSynced,
    runningConversationIds,
    tabInitialKey: initialKey,
    tabOpenIds: openIds,
    setTabOpenIds: setOpenIds,
    tabEmptyKeys: emptyKeys,
    setTabEmptyKeys: setEmptyKeys,
    tabEmptyTitles: emptyTitles,
    setTabEmptyTitles: setEmptyTitles,
    tabActiveEmptyKey: activeEmptyKey,
    setTabActiveEmptyKey: setActiveEmptyKey,
  } = useAiChat();
  const conversationsQuery = useConversations({ q: "", includeArchived: false });
  const conversationsById = useMemo(() => {
    const map = new Map<string, Conversation>();
    for (const conversation of flattenConversations(conversationsQuery.data)) map.set(conversation.id, conversation);
    return map;
  }, [conversationsQuery.data]);
  const readMarks = useConversationReadMarks();
  const { rename } = useRenameChat();

  // Having a conversation on screen reads it. Re-mark when its live run ends or the
  // list catches up with a newer `updated`, so a turn that finishes while the user is
  // looking never flips to unread; one that finishes after they left does.
  const activeIsOnScreen = conversationVisible && activeConversationId !== null && activeEmptyKey === null;
  const activeIsRunning = activeConversationId !== null && runningConversationIds.has(activeConversationId);
  const activeUpdatedMs =
    activeConversationId !== null ? (conversationsById.get(activeConversationId)?.updated.getTime() ?? 0) : 0;
  useEffect(() => {
    if (activeConversationId === null || !activeIsOnScreen || activeIsRunning) return;
    // Floor at Date.now() so a conversation not yet in the list (activeUpdatedMs 0) still
    // marks read. Trade-off: if the server clock lags the client, a newer turn whose server
    // `updated` falls below this mark reads as seen. Acceptable vs. never clearing the dot.
    markConversationRead(activeConversationId, Math.max(Date.now(), activeUpdatedMs));
  }, [activeConversationId, activeIsOnScreen, activeIsRunning, activeUpdatedMs]);

  // Reconcile external changes to the active conversation into the tab list: a first
  // send turns the active empty tab into a saved one; a sidebar select adds a saved
  // tab; a sidebar "New" (active → null with no empty tab) opens a fresh empty tab.
  useEffect(() => {
    if (activeConversationId !== null) {
      if (activeEmptyKey !== null) {
        setEmptyKeys((prev) => prev.filter((key) => key !== activeEmptyKey));
        setEmptyTitles((prev) => omitKey(prev, activeEmptyKey));
        setActiveEmptyKey(null);
      }
      setOpenIds((prev) => (prev.includes(activeConversationId) ? prev : [...prev, activeConversationId]));
    } else if (activeEmptyKey === null) {
      const key = crypto.randomUUID();
      setEmptyKeys((prev) => [...prev, key]);
      setActiveEmptyKey(key);
    }
  }, [activeConversationId, activeEmptyKey, setOpenIds, setEmptyKeys, setEmptyTitles, setActiveEmptyKey]);

  const switchTo = (key: string) => {
    if (emptyKeys.includes(key)) {
      if (key === activeEmptyKey) return;
      if (activeConversationId !== null) markConversationSynced(activeConversationId);
      setActiveEmptyKey(key);
      setActiveConversationId(null);
      setChatTitle(emptyTitles[key] ?? null);
      clearMessages();
    } else {
      if (key === activeConversationId && activeEmptyKey === null) return;
      if (activeConversationId !== null) markConversationSynced(activeConversationId);
      setActiveEmptyKey(null);
      // The provider swaps the transcript with the id: a streaming conversation's is kept aside
      // and comes back on return; anything else starts empty for hydration to fill.
      setActiveConversationId(key);
      setChatTitle(conversationsById.get(key)?.title ?? null);
    }
  };

  const startNew = () => {
    if (activeConversationId !== null) markConversationSynced(activeConversationId);
    const key = crypto.randomUUID();
    setEmptyKeys((prev) => [...prev, key]);
    setActiveEmptyKey(key);
    startNewConversation();
  };

  // Move to the first available tab after a close, or a fresh empty chat if none remain.
  const switchToFirst = (primary: string | null, secondary: string | null) => {
    const target = primary ?? secondary;
    if (target !== null) switchTo(target);
    else startNew();
  };

  const closeTab = (key: string) => {
    clearComposerDraft(key);
    if (emptyKeys.includes(key)) {
      const remaining = emptyKeys.filter((openKey) => openKey !== key);
      setEmptyKeys(remaining);
      setEmptyTitles((prev) => omitKey(prev, key));
      if (key === activeEmptyKey) switchToFirst(remaining.at(-1) ?? null, openIds.at(-1) ?? null);
    } else {
      const remaining = openIds.filter((id) => id !== key);
      setOpenIds(remaining);
      if (key === activeConversationId && activeEmptyKey === null) {
        switchToFirst(remaining.at(-1) ?? null, emptyKeys.at(-1) ?? null);
      }
    }
  };

  // A saved chat renames on the server. An unsaved one has no server copy yet: its title lives in
  // `chatTitle`, which seeds the create on first send, and in `emptyTitles`, which keeps it across a
  // switch. The strip selects a tab before editing it, so an off-screen one is never renamed.
  const renameTab = (key: string, title: string | null) => {
    if (!emptyKeys.includes(key)) {
      rename(key, title);
      return;
    }
    if (key !== activeEmptyKey) return;
    setChatTitle(title);
    setEmptyTitles((prev) => ({ ...prev, [key]: title }));
  };

  const savedTabs: ChatTab[] = openIds.map((id) => {
    const isActive = id === activeConversationId && activeEmptyKey === null;
    const conversation = conversationsById.get(id);
    return {
      key: id,
      title: savedTabTitle(conversation, isActive, chatTitle),
      status: runningConversationIds.has(id) ? "running" : (conversation?.status ?? null),
      isActive,
      isRead: isActive || conversation === undefined || !hasUnseenUpdate(readMarks, conversation),
    };
  });
  const emptyTabs: ChatTab[] = emptyKeys.map((key) => ({
    key,
    title: key === activeEmptyKey ? chatTitle : (emptyTitles[key] ?? null),
    status: null,
    isActive: key === activeEmptyKey,
    isRead: true,
  }));

  const activeKey = activeEmptyKey ?? activeConversationId ?? initialKey;

  return { tabs: [...savedTabs, ...emptyTabs], activeKey, switchTo, closeTab, startNew, renameTab };
}

// The list is the source of truth once it holds the conversation (renames patch it optimistically);
// `chatTitle` only covers an on-screen chat the list has not loaded yet. A null list title is a
// title the user cleared, so it must not fall back.
function savedTabTitle(conversation: Conversation | undefined, isActive: boolean, chatTitle: string | null) {
  if (conversation !== undefined) return conversation.title;
  return isActive ? chatTitle : null;
}

function omitKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([entryKey]) => entryKey !== key));
}
